import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Grid,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  FolderOpenOutlined,
  PhoneOutlined,
  ReloadOutlined,
  UserSwitchOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import {
  assignAdminPublicLead,
  assignAdminPublicLeadsBulk,
  createAgentPublicLeadNote,
  deleteAgentPublicLead,
  fetchAgentPublicLeads,
  patchAgentPublicLead,
} from "../../../services/publicSiteLeadsApi";
import { navigateAgentPackageUpgrade } from "../../../utils/agentUpgradeNavigation";
import { buildWhatsAppChatUrl } from "../../../utils/whatsappDeepLink";
import { useAgentEntitlements } from "../../../features/agentEntitlements/useAgentEntitlements";
import { isAdminUser } from "../../../services/authService";
import { getAgents } from "../../../services/fhpService";
import { startCrmLeadOfferHandoff } from "../../../utils/crmLeadHandoff";
import {
  CRM_LEAD_STATUS_OPTIONS,
  formatLeadSourceType,
  formatPublicLeadCategory,
  isCrmDocketLead,
  isCrmLeadStatusNew,
  isCrmLeadUnassigned,
} from "./crmLeadFormatters";
import "./CrmHubPage.css";

function formatShortDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

/**
 * Active agents only for CRM assign.
 * Gates: agents.activated (same as Admin.jsx recommended lists / billing status=active)
 * and exclude management_status|managementStatus === "paused" (AgentsManagement).
 */
function isAssignableActiveAgent(agent) {
  if (!agent || typeof agent !== "object") return false;
  const activated = agent.activated;
  if (
    activated === false ||
    activated === 0 ||
    activated === "0" ||
    activated === "false"
  ) {
    return false;
  }
  const mgmt = String(
    agent.management_status ?? agent.managementStatus ?? ""
  )
    .trim()
    .toLowerCase();
  if (mgmt === "paused" || mgmt === "inactive" || mgmt === "pending") {
    return false;
  }
  if (activated != null && activated !== "") {
    return Boolean(activated);
  }
  // Legacy /getAgents rows without activated: keep unless paused above
  return true;
}

function buildAgentAssignOption({ id, name, email }) {
  const emailText = email ? String(email) : "";
  const metaLine = emailText ? `#${id} · ${emailText}` : `#${id}`;
  const searchText = `${name} ${id} ${emailText}`.trim();
  return {
    value: Number(id),
    // Compact single-line for the closed Select trigger
    label: name,
    name,
    email: emailText,
    searchText,
    metaLine,
  };
}

function normalizeAgentSelectOptions(rawAgents) {
  const payloadCandidates = [
    rawAgents,
    rawAgents?.data,
    rawAgents?.data?.data,
    rawAgents?.data?.agents,
    rawAgents?.agents,
    rawAgents?.result,
    rawAgents?.result?.data,
    rawAgents?.result?.agents,
  ];
  const list = payloadCandidates.find((item) => Array.isArray(item)) || [];
  return list
    .filter(isAssignableActiveAgent)
    .map((agent) => {
      const id = agent?.id ?? agent?.agent_id ?? agent?.agentId;
      if (id == null || id === "") return null;
      const email = agent?.email ?? agent?.mail ?? null;
      const fullName = (
        agent?.name ||
        agent?.fullname ||
        `${agent?.firstname || ""} ${agent?.lastname || ""}`
      )
        .trim()
        .replace(/\s+/g, " ");
      const name = fullName || `סוכן #${id}`;
      return buildAgentAssignOption({ id, name, email });
    })
    .filter((opt) => opt && Number.isFinite(opt.value))
    .sort((a, b) => String(a.name).localeCompare(String(b.name), "he"));
}

function LeadSourceCell({ row }) {
  const meta = formatLeadSourceType(row);
  const isMeta = meta.className === "crm-badge--meta";
  return (
    <div className={isMeta ? "crm-lead-source crm-lead-source--meta" : "crm-lead-source"}>
      {isMeta && meta.detail ? (
        <>
          <div className="crm-lead-source__campaign" title={meta.detail}>
            {meta.detail}
          </div>
          <span className={`crm-badge ${meta.className}`}>{meta.label}</span>
        </>
      ) : (
        <>
          <span className={`crm-badge ${meta.className}`}>{meta.label}</span>
          {meta.detail ? (
            <div className="crm-lead-source__detail" title={meta.detail}>
              {meta.detail}
            </div>
          ) : null}
        </>
      )}
      {row.source_url ? (
        <div style={{ marginTop: 6 }}>
          <a
            href={row.source_url}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
          >
            עמוד מקור
          </a>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Shared leads workspace used by CRM Hub tab and legacy /ai-sales-leads page.
 * @param {"leads"|"dockets"|"all"} [variant]
 */
export default function CrmLeadsWorkspace({
  embedded = false,
  headerExtra = null,
  showCreateOffer = false,
  title = "לידים / AI Sales",
  onOpenLead = null,
  variant = "all",
}) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const messageRef = useRef(message);
  messageRef.current = message;
  const screens = Grid.useBreakpoint();
  /** Table columns are too wide below lg — use stacked cards on tablet/phone. */
  const useCompactList = !screens.lg;
  const {
    status: entitlementsStatus,
    canUseLeadsSystem,
    loading: entitlementsLoading,
    refresh: refreshEntitlements,
  } = useAgentEntitlements();
  const leadsAllowed = canUseLeadsSystem;

  const [leads, setLeads] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [savingStatusLeadId, setSavingStatusLeadId] = useState(null);
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [assigningLeadId, setAssigningLeadId] = useState(null);
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedLeadKeys, setSelectedLeadKeys] = useState([]);
  const [bulkAssignAgentId, setBulkAssignAgentId] = useState(undefined);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  // Admins: default to system-wide leads/dockets (not only "mine")
  const [leadsScope, setLeadsScope] = useState(() => {
    if (!isAdminUser()) return "mine";
    try {
      const stored = sessionStorage.getItem("crm_leads_scope");
      if (stored === "mine" || stored === "all") return stored;
    } catch {
      /* ignore */
    }
    return "all";
  });
  const isAdmin = isAdminUser();
  const loadSeqRef = useRef(0);
  const showAllAgentsScope = isAdmin && leadsScope === "all";
  const canBulkAssign = showAllAgentsScope;

  useEffect(() => {
    if (!isAdmin) return;
    try {
      sessionStorage.setItem("crm_leads_scope", leadsScope);
    } catch {
      /* ignore */
    }
  }, [isAdmin, leadsScope]);

  useEffect(() => {
    setSelectedLeadKeys([]);
    setBulkAssignAgentId(undefined);
  }, [leadsScope, statusFilter, variant, showAllAgentsScope]);

  const statusOptions = CRM_LEAD_STATUS_OPTIONS;
  const statusLabelMap = useMemo(
    () =>
      statusOptions.reduce((acc, item) => {
        acc[item.value] = item;
        return acc;
      }, {}),
    [statusOptions]
  );
  const isDocketsVariant = variant === "dockets";
  const visibleLeads = useMemo(() => {
    if (variant === "dockets") return leads.filter(isCrmDocketLead);
    if (variant === "leads") return leads.filter((lead) => !isCrmDocketLead(lead));
    return leads;
  }, [leads, variant]);

  const newLeadsCount = useMemo(
    () => visibleLeads.filter(isCrmLeadStatusNew).length,
    [visibleLeads]
  );
  const unassignedVisibleLeads = useMemo(
    () => visibleLeads.filter(isCrmLeadUnassigned),
    [visibleLeads]
  );
  const unassignedVisibleCount = unassignedVisibleLeads.length;
  const newLeadsBadgeText = isDocketsVariant
    ? newLeadsCount === 1
      ? "דוקט חדש"
      : `${newLeadsCount} דוקטים חדשים`
    : newLeadsCount === 1
      ? "ליד חדש"
      : `${newLeadsCount} לידים חדשים`;

  const openLead = useCallback(
    (lead) => {
      if (!lead?.id) return;
      if (typeof onOpenLead === "function") {
        onOpenLead(lead);
        return;
      }
    },
    [onOpenLead]
  );

  const loadList = useCallback(async () => {
    if (!leadsAllowed) return;
    const seq = ++loadSeqRef.current;
    setListLoading(true);
    setListError("");
    try {
      const res = await fetchAgentPublicLeads({
        status: statusFilter || undefined,
        limit: 100,
        scope: isAdmin && leadsScope === "all" ? "all" : undefined,
        kind: variant === "dockets" ? "dockets" : undefined,
      });
      if (seq !== loadSeqRef.current) return;
      if (!res?.success) {
        const apiMsg =
          res?.message_he ||
          res?.message ||
          (typeof res?.error === "string" ? res.error : res?.error?.message) ||
          "שגיאה בטעינת לידים";
        throw new Error(apiMsg);
      }
      setLeads(Array.isArray(res?.leads) ? res.leads : []);
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      const code = String(e?.code || "");
      const raw = String(
        e?.response?.data?.message_he ||
          e?.response?.data?.message ||
          e?.message ||
          ""
      );
      const isTimeout =
        code === "ECONNABORTED" ||
        /timeout/i.test(raw) ||
        /timed out/i.test(raw);
      const apiMsg = isTimeout
        ? "השרת לא הגיב בזמן. בדקו שה-Backend רץ (פורט 8080) ורעננו את העמוד."
        : raw || "שגיאת רשת";
      setListError(apiMsg);
      setLeads([]);
      messageRef.current?.error?.(apiMsg);
    } finally {
      if (seq === loadSeqRef.current) setListLoading(false);
    }
  }, [isAdmin, leadsAllowed, leadsScope, statusFilter, variant]);

  useEffect(() => {
    if (entitlementsStatus === "idle" || entitlementsStatus === "error") {
      void refreshEntitlements();
    }
  }, [entitlementsStatus, refreshEntitlements]);

  useEffect(() => {
    if (!leadsAllowed) {
      setLeads([]);
      setListError("");
      setListLoading(false);
      return;
    }
    // Wait until entitlements resolved so agents don't hit the API mid-gate.
    if (entitlementsStatus !== "ok" && !isAdmin) return;
    void loadList();
  }, [entitlementsStatus, isAdmin, leadsAllowed, loadList]);

  useEffect(() => {
    if (!showAllAgentsScope) return;
    let cancelled = false;
    setAgentsLoading(true);
    getAgents()
      .then((raw) => {
        if (cancelled) return;
        setAgentOptions(normalizeAgentSelectOptions(raw));
      })
      .catch(() => {
        if (cancelled) return;
        setAgentOptions([]);
        messageRef.current?.warning?.("לא ניתן לטעון רשימת סוכנים להקצאה");
      })
      .finally(() => {
        if (!cancelled) setAgentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showAllAgentsScope]);

  const applyAssignedAgentToRows = useCallback(
    (leadIds, nextAgentId, leadsById = {}) => {
      const idSet = new Set((leadIds || []).map((id) => String(id)));
      const agentOpt =
        nextAgentId == null
          ? null
          : agentOptions.find((opt) => Number(opt.value) === Number(nextAgentId));
      setLeads((prev) =>
        prev.map((row) => {
          const key = String(row.id);
          if (!idSet.has(key)) return row;
          const updated = leadsById[key];
          return {
            ...row,
            ...(updated || {}),
            agent_id: nextAgentId,
            agent_name:
              nextAgentId == null
                ? null
                : agentOpt?.name || updated?.agent_name || row.agent_name || null,
            agent_email:
              nextAgentId == null
                ? null
                : agentOpt?.email ||
                  updated?.agent_email ||
                  row.agent_email ||
                  null,
          };
        })
      );
    },
    [agentOptions]
  );

  const onAssignAgent = useCallback(
    async (leadId, agentId) => {
      if (!isAdmin || !leadId) return;
      const nextAgentId =
        agentId == null || agentId === "" ? null : Number(agentId);
      if (nextAgentId != null && !Number.isFinite(nextAgentId)) return;
      setAssigningLeadId(leadId);
      try {
        const { data } = await assignAdminPublicLead(leadId, nextAgentId);
        const updated = data?.lead;
        if (updated?.id) {
          applyAssignedAgentToRows(
            [leadId],
            nextAgentId,
            { [String(updated.id)]: updated }
          );
        } else {
          await loadList();
        }
        window.dispatchEvent(new CustomEvent("publicLeadsStatusChanged"));
        messageRef.current?.success?.(
          nextAgentId == null
            ? "הקצאת הליד הוסרה — הוחזר למאגר"
            : "הליד הוקצה לסוכן"
        );
      } catch (e) {
        const apiMsg =
          e?.response?.data?.message ||
          e?.message ||
          (nextAgentId == null ? "שגיאה בהסרת הקצאה" : "שגיאה בהקצאת הליד");
        messageRef.current?.error?.(apiMsg);
      } finally {
        setAssigningLeadId(null);
      }
    },
    [applyAssignedAgentToRows, isAdmin, loadList]
  );

  const onBulkAssignAgent = useCallback(async () => {
    if (!canBulkAssign || bulkAssigning) return;
    const ids = selectedLeadKeys.map((id) => String(id)).filter(Boolean);
    if (!ids.length) return;
    const nextAgentId =
      bulkAssignAgentId == null || bulkAssignAgentId === ""
        ? null
        : Number(bulkAssignAgentId);
    if (nextAgentId != null && !Number.isFinite(nextAgentId)) {
      messageRef.current?.warning?.("בחרו סוכן להעברה");
      return;
    }
    if (nextAgentId == null) {
      messageRef.current?.warning?.("בחרו סוכן להעברה");
      return;
    }
    setBulkAssigning(true);
    try {
      const { okIds, failed, leadsById } = await assignAdminPublicLeadsBulk(
        ids,
        nextAgentId
      );
      if (okIds.length) {
        applyAssignedAgentToRows(okIds, nextAgentId, leadsById);
        window.dispatchEvent(new CustomEvent("publicLeadsStatusChanged"));
      }
      if (failed.length && okIds.length) {
        messageRef.current?.warning?.(
          `${okIds.length} לידים הועברו, ${failed.length} נכשלו`
        );
      } else if (failed.length) {
        messageRef.current?.error?.(
          failed[0]?.message || "שגיאה בהעברה מרובה"
        );
      } else {
        const agentName =
          agentOptions.find((opt) => Number(opt.value) === Number(nextAgentId))
            ?.name || "הסוכן";
        messageRef.current?.success?.(
          okIds.length === 1
            ? `ליד אחד הועבר לסוכן ${agentName}`
            : `${okIds.length} לידים הועברו לסוכן ${agentName}`
        );
      }
      if (okIds.length) {
        const failedSet = new Set(failed.map((item) => String(item.leadId)));
        setSelectedLeadKeys((prev) =>
          prev.filter((key) => failedSet.has(String(key)))
        );
      }
    } catch (e) {
      messageRef.current?.error?.(
        e?.response?.data?.message || e?.message || "שגיאה בהעברה מרובה"
      );
    } finally {
      setBulkAssigning(false);
    }
  }, [
    agentOptions,
    applyAssignedAgentToRows,
    bulkAssignAgentId,
    bulkAssigning,
    canBulkAssign,
    selectedLeadKeys,
  ]);

  const toggleLeadSelected = useCallback((leadId, checked) => {
    const key = String(leadId || "");
    if (!key) return;
    setSelectedLeadKeys((prev) => {
      const has = prev.some((item) => String(item) === key);
      if (checked && !has) return [...prev, key];
      if (!checked && has) return prev.filter((item) => String(item) !== key);
      return prev;
    });
  }, []);

  const selectAllUnassignedLeads = useCallback(() => {
    setSelectedLeadKeys(
      unassignedVisibleLeads
        .map((row) => row.id)
        .filter((id) => id != null && id !== "")
    );
  }, [unassignedVisibleLeads]);

  const clearLeadSelection = useCallback(() => {
    setSelectedLeadKeys([]);
  }, []);

  const renderAssignSelect = useCallback(
    (row, wrapClassName = "crm-assign-select-wrap") => {
      const currentId = row.agent_id != null ? Number(row.agent_id) : null;
      let options = agentOptions;
      if (
        currentId != null &&
        Number.isFinite(currentId) &&
        !agentOptions.some((opt) => Number(opt.value) === currentId)
      ) {
        // Keep current assignee visible even if inactive / paused (not in active list)
        const fallbackName = row.agent_name || `סוכן #${currentId}`;
        options = [
          buildAgentAssignOption({
            id: currentId,
            name: fallbackName,
            email: row.agent_email || "",
          }),
          ...agentOptions,
        ];
      }
      return (
        <div className={wrapClassName} onClick={(e) => e.stopPropagation()}>
          <Select
            size="small"
            showSearch
            allowClear
            placeholder="הקצה לסוכן"
            className="crm-assign-select"
            popupClassName="crm-assign-select-dropdown"
            popupMatchSelectWidth={false}
            options={options}
            optionRender={(option) => {
              const data = option?.data || {};
              return (
                <div className="crm-assign-option">
                  <div className="crm-assign-option__name">
                    {data.name || data.label || "—"}
                  </div>
                  <div className="crm-assign-option__meta">
                    {data.metaLine ||
                      (data.value != null ? `#${data.value}` : "")}
                  </div>
                </div>
              );
            }}
            value={currentId != null && Number.isFinite(currentId) ? currentId : undefined}
            loading={agentsLoading || assigningLeadId === row.id || bulkAssigning}
            disabled={assigningLeadId === row.id || bulkAssigning}
            filterOption={(input, option) => {
              const q = String(input || "").trim().toLowerCase();
              if (!q) return true;
              const hay = String(
                option?.searchText || option?.data?.searchText || option?.name || ""
              ).toLowerCase();
              return hay.includes(q);
            }}
            title={currentId != null ? "שנה סוכן או הסר הקצאה (נקה)" : undefined}
            onChange={(nextAgentId) => onAssignAgent(row.id, nextAgentId ?? null)}
          />
        </div>
      );
    },
    [agentOptions, agentsLoading, assigningLeadId, bulkAssigning, onAssignAgent]
  );

  const onStatusChange = useCallback(
    async (leadId, status) => {
      if (!leadsAllowed) return;
      const previous = leads;
      setLeads((prev) =>
        prev.map((item) => (item.id === leadId ? { ...item, status } : item))
      );
      setSavingStatusLeadId(leadId);
      try {
        const res = await patchAgentPublicLead(leadId, { status });
        if (!res?.success) throw new Error(res?.error?.message || "עדכון ליד נכשל");
        window.dispatchEvent(new CustomEvent("publicLeadsStatusChanged"));
      } catch (e) {
        setLeads(previous);
        message.error(e?.message || "שגיאת רשת");
      } finally {
        setSavingStatusLeadId(null);
      }
    },
    [leads, leadsAllowed, message]
  );

  const onDeleteLead = useCallback(
    async (lead) => {
      if (!leadsAllowed || !lead?.id) return;
      const entityLabel = isDocketsVariant || isCrmDocketLead(lead) ? "דוקט" : "ליד";
      setDeletingLeadId(lead.id);
      try {
        const res = await deleteAgentPublicLead(lead.id);
        if (!res?.success) throw new Error(res?.error?.message || res?.message || "מחיקה נכשלה");
        setLeads((prev) => prev.filter((item) => item.id !== lead.id));
        message.success(isDocketsVariant ? "הדוקט נמחק" : "הליד נמחק");
        window.dispatchEvent(new CustomEvent("publicLeadsStatusChanged"));
      } catch (e) {
        message.error(
          e?.response?.data?.message || e?.message || `שגיאה במחיקת ${entityLabel}`
        );
      } finally {
        setDeletingLeadId(null);
      }
    },
    [isDocketsVariant, leadsAllowed, message]
  );

  const onUpgradePackage = useCallback(() => {
    if (isAdminUser()) {
      navigate("/p/dashboard/admin/package-features");
      return;
    }
    navigateAgentPackageUpgrade(navigate, "ai_sales_leads_upgrade");
  }, [navigate]);

  const onCreateOffer = useCallback(
    async (lead) => {
      if (!lead?.id) return;
      try {
        await createAgentPublicLeadNote(lead.id, {
          note_text: "התחיל יצירת הצעה מה-CRM",
        });
      } catch {
        // continue
      }
      startCrmLeadOfferHandoff(navigate, lead);
    },
    [navigate]
  );

  const openWhatsApp = (phone) => {
    const url = buildWhatsAppChatUrl(phone);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const openCall = (phone) => {
    const text = String(phone || "").trim();
    if (!text) return;
    window.open(`tel:${text}`, "_self");
  };

  const renderRowActions = useCallback(
    (row) => (
      <div className="crm-row-actions" onClick={(e) => e.stopPropagation()}>
        <div className="crm-row-actions__btns">
          <Tooltip title="התקשר">
            <Button
              size="small"
              icon={<PhoneOutlined />}
              onClick={() => openCall(row.phone)}
              disabled={!row.phone}
            />
          </Tooltip>
          <Tooltip title="WhatsApp">
            <Button
              size="small"
              icon={<WhatsAppOutlined />}
              onClick={() => openWhatsApp(row.phone)}
              disabled={!row.phone}
            />
          </Tooltip>
          <Button
            size="small"
            icon={<FolderOpenOutlined />}
            onClick={() => openLead(row)}
          >
            {isDocketsVariant ? "פתח דוקט" : "תיק"}
          </Button>
          {showCreateOffer && !showAllAgentsScope ? (
            <Button size="small" type="primary" onClick={() => onCreateOffer(row)}>
              צור הצעה
            </Button>
          ) : null}
          {!showAllAgentsScope || isAdmin ? (
            <Popconfirm
              title={isDocketsVariant ? "למחוק את הדוקט?" : "למחוק את הליד?"}
              description={`${row.full_name || (isDocketsVariant ? "דוקט זה" : "ליד זה")} יוסר לצמיתות.`}
              okText="מחק"
              cancelText="ביטול"
              okButtonProps={{ danger: true, loading: deletingLeadId === row.id }}
              onConfirm={() => onDeleteLead(row)}
            >
              <Tooltip title={isDocketsVariant ? "מחק דוקט" : "מחק ליד"}>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingLeadId === row.id}
                  aria-label={isDocketsVariant ? "מחק דוקט" : "מחק ליד"}
                />
              </Tooltip>
            </Popconfirm>
          ) : null}
        </div>
        {!showAllAgentsScope && (
          <Select
            size="small"
            value={row.status || "new"}
            options={statusOptions}
            loading={savingStatusLeadId === row.id}
            onChange={(nextStatus) => onStatusChange(row.id, nextStatus)}
          />
        )}
      </div>
    ),
    [
      deletingLeadId,
      isAdmin,
      isDocketsVariant,
      onCreateOffer,
      onDeleteLead,
      onStatusChange,
      openLead,
      savingStatusLeadId,
      showAllAgentsScope,
      showCreateOffer,
      statusOptions,
    ]
  );

  const columns = useMemo(() => {
    const agentCols = showAllAgentsScope
      ? [
          {
            title: "סוכן",
            key: "agent",
            width: 210,
            render: (_, row) => renderAssignSelect(row),
          },
        ]
      : [];

    return [
      ...agentCols,
      {
        title: "תאריך",
        dataIndex: "created_at",
        key: "created_at",
        width: 130,
        render: (v) => <span className="crm-lead-date">{formatShortDate(v)}</span>,
      },
      {
        title: "לקוח",
        key: "lead",
        width: 200,
        render: (_, row) => (
          <div>
            <div className="crm-lead-customer__name">{row.full_name || "—"}</div>
            <div className="crm-lead-customer__dest">
              {row.interest_destination || "ללא יעד"}
            </div>
            <div className="crm-lead-customer__meta">
              {formatPublicLeadCategory(row.category)}
            </div>
          </div>
        ),
      },
      ...(showAllAgentsScope
        ? []
        : [
            {
              title: "סיכום",
              key: "ai_note",
              render: (_, row) => (
                <div className="crm-lead-note">{row.ai_sales_note || "—"}</div>
              ),
            },
          ]),
      {
        title: "יצירת קשר",
        key: "contact",
        width: 160,
        render: (_, row) => (
          <div className="crm-lead-contact">
            <span>{row.phone || "—"}</span>
            <span className="crm-lead-contact__email">{row.email || "—"}</span>
          </div>
        ),
      },
      {
        title: "מקור",
        key: "source",
        width: 200,
        render: (_, row) => <LeadSourceCell row={row} />,
      },
      ...(showAllAgentsScope
        ? [
            {
              title: "סטטוס",
              dataIndex: "status",
              key: "status",
              width: 110,
              render: (v) => {
                const key = v || "new";
                const meta = statusLabelMap[key];
                return (
                  <span className={`crm-badge crm-badge--status-${key}`}>
                    {meta?.label || key}
                  </span>
                );
              },
            },
          ]
        : []),
      {
        title: "פעולות",
        key: "actions",
        width: showCreateOffer && !showAllAgentsScope ? 260 : 220,
        render: (_, row) => renderRowActions(row),
      },
    ];
  }, [
    renderAssignSelect,
    renderRowActions,
    showAllAgentsScope,
    showCreateOffer,
    statusLabelMap,
  ]);

  if (entitlementsLoading || entitlementsStatus === "idle") {
    if (!canUseLeadsSystem) {
      return (
        <div
          className="crm-leads-workspace"
          style={{
            padding: embedded ? 24 : 48,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" />
        </div>
      );
    }
  }

  if (!leadsAllowed) {
    return (
      <div className="crm-leads-workspace" style={{ padding: embedded ? 0 : 16 }}>
        <Card className="crm-locked-card">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              מערכת הלידים זמינה בחבילת PRO
            </Typography.Title>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              שדרגו את החבילה כדי לקבל גישה ללידים, ניהול סטטוסים, תיעוד ומעקב.
            </Typography.Paragraph>
            <Button type="primary" onClick={onUpgradePackage}>
              שדרוג חבילה
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div className="crm-leads-workspace" style={{ padding: embedded ? 0 : 16 }}>
      <div className="crm-leads-toolbar">
        <div className="crm-leads-toolbar__title-block">
          <Typography.Title level={embedded ? 4 : 3} className="crm-leads-toolbar__title">
            {title}
          </Typography.Title>
          {newLeadsCount > 0 && !showAllAgentsScope ? (
            <span className="crm-badge crm-badge--new">{newLeadsBadgeText}</span>
          ) : null}
          {showAllAgentsScope ? (
            <span className="crm-badge crm-badge--admin">
              {isDocketsVariant ? "כל הדוקטים במערכת" : "כל הלידים במערכת"}
            </span>
          ) : null}
        </div>
        <div className="crm-leads-toolbar__actions">
          {headerExtra}
          {isAdmin ? (
            <Button
              type={leadsScope === "all" ? "primary" : "default"}
              onClick={() => setLeadsScope((prev) => (prev === "all" ? "mine" : "all"))}
            >
              {leadsScope === "all"
                ? isDocketsVariant
                  ? "הדוקטים שלי"
                  : "הלידים שלי"
                : isDocketsVariant
                  ? "כל הדוקטים במערכת"
                  : "כל הלידים במערכת"}
            </Button>
          ) : null}
          <Select
            allowClear
            placeholder="סינון סטטוס"
            className="crm-leads-toolbar__status"
            value={statusFilter || undefined}
            options={statusOptions}
            onChange={(v) => setStatusFilter(v || "")}
          />
          <Button icon={<ReloadOutlined />} onClick={loadList}>
            רענון
          </Button>
        </div>
      </div>

      <div className={`crm-table-panel${useCompactList ? " crm-table-panel--cards" : ""}`}>
        {listError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={listError}
            action={
              <Button size="small" onClick={() => void loadList()}>
                נסה שוב
              </Button>
            }
          />
        ) : null}
        {canBulkAssign && selectedLeadKeys.length > 0 ? (
          <div className="crm-bulk-assign-bar" role="region" aria-label="העברה מרובה לסוכן">
            <div className="crm-bulk-assign-bar__count">
              נבחרו <strong>{selectedLeadKeys.length}</strong>{" "}
              {isDocketsVariant ? "דוקטים" : "לידים"}
            </div>
            <div className="crm-bulk-assign-bar__controls">
              <Select
                showSearch
                allowClear
                placeholder="בחרו סוכן להעברה"
                className="crm-bulk-assign-bar__agent"
                popupClassName="crm-assign-select-dropdown"
                popupMatchSelectWidth={false}
                options={agentOptions}
                optionRender={(option) => {
                  const data = option?.data || {};
                  return (
                    <div className="crm-assign-option">
                      <div className="crm-assign-option__name">
                        {data.name || data.label || "—"}
                      </div>
                      <div className="crm-assign-option__meta">
                        {data.metaLine ||
                          (data.value != null ? `#${data.value}` : "")}
                      </div>
                    </div>
                  );
                }}
                value={bulkAssignAgentId}
                loading={agentsLoading || bulkAssigning}
                disabled={bulkAssigning}
                filterOption={(input, option) => {
                  const q = String(input || "").trim().toLowerCase();
                  if (!q) return true;
                  const hay = String(
                    option?.searchText || option?.data?.searchText || option?.name || ""
                  ).toLowerCase();
                  return hay.includes(q);
                }}
                onChange={(value) => setBulkAssignAgentId(value)}
              />
              <Button
                type="primary"
                icon={<UserSwitchOutlined />}
                loading={bulkAssigning}
                disabled={
                  bulkAssigning ||
                  bulkAssignAgentId == null ||
                  bulkAssignAgentId === ""
                }
                onClick={() => void onBulkAssignAgent()}
              >
                העבר לסוכן ({selectedLeadKeys.length})
              </Button>
              <Button
                disabled={bulkAssigning || unassignedVisibleCount === 0}
                onClick={selectAllUnassignedLeads}
              >
                בחר את כל הלא מוקצים ({unassignedVisibleCount})
              </Button>
              <Button disabled={bulkAssigning} onClick={clearLeadSelection}>
                נקה בחירה
              </Button>
            </div>
          </div>
        ) : canBulkAssign && unassignedVisibleCount > 0 ? (
          <div className="crm-bulk-assign-hint">
            סמנו לידים שלא הוקצו והעבירו לסוכן ספציפי בפעולה אחת
            <Button type="link" size="small" onClick={selectAllUnassignedLeads}>
              בחר את כל הלא מוקצים ({unassignedVisibleCount})
            </Button>
          </div>
        ) : null}
        {useCompactList ? (
          <Spin spinning={listLoading}>
            {visibleLeads.length === 0 && !listLoading ? (
              <div className="crm-leads-empty">
                {listError
                  ? " "
                    : isDocketsVariant
                    ? "עדיין אין דוקטים מהזמנות ששולמו או ממתינות להעברה בנקאית"
                    : "אין לידים להצגה"}
              </div>
            ) : (
              <div className="crm-leads-cards" role="list">
                {visibleLeads.map((row) => {
                  const statusKey = row.status || "new";
                  const statusMeta = statusLabelMap[statusKey];
                  const rowKey = String(row.id);
                  const isSelected = selectedLeadKeys.some(
                    (key) => String(key) === rowKey
                  );
                  return (
                    <article
                      key={row.id}
                      className={`crm-lead-card${isSelected ? " crm-lead-card--selected" : ""}`}
                      role="listitem"
                      onClick={() => openLead(row)}
                    >
                      <div className="crm-lead-card__head">
                        <div className="crm-lead-card__identity">
                          {canBulkAssign ? (
                            <div
                              className="crm-lead-card__select"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled={bulkAssigning}
                                onChange={(e) =>
                                  toggleLeadSelected(row.id, e.target.checked)
                                }
                              />
                            </div>
                          ) : null}
                          <div className="crm-lead-customer__name">
                            {row.full_name || "—"}
                          </div>
                          <div className="crm-lead-customer__dest">
                            {row.interest_destination || "ללא יעד"}
                          </div>
                          <div className="crm-lead-customer__meta">
                            {formatPublicLeadCategory(row.category)}
                            {showAllAgentsScope && row.agent_name
                              ? ` · ${row.agent_name}`
                              : ""}
                          </div>
                          {showAllAgentsScope
                            ? renderAssignSelect(row, "crm-lead-card__assign")
                            : null}
                        </div>
                        <div className="crm-lead-card__meta-side">
                          <span className="crm-lead-date">
                            {formatShortDate(row.created_at)}
                          </span>
                          <LeadSourceCell row={row} />
                          {showAllAgentsScope ? (
                            <span className={`crm-badge crm-badge--status-${statusKey}`}>
                              {statusMeta?.label || statusKey}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {row.ai_sales_note && !showAllAgentsScope ? (
                        <div className="crm-lead-card__note">{row.ai_sales_note}</div>
                      ) : null}
                      <div className="crm-lead-card__contact">
                        <span>{row.phone || "—"}</span>
                        <span className="crm-lead-contact__email">{row.email || "—"}</span>
                      </div>
                      {renderRowActions(row)}
                    </article>
                  );
                })}
              </div>
            )}
            {visibleLeads.length > 0 ? (
              <div className="crm-leads-cards__total">
                {isDocketsVariant
                  ? `${visibleLeads.length} דוקטים`
                  : `${visibleLeads.length} לידים`}
              </div>
            ) : null}
          </Spin>
        ) : (
          <Table
            rowKey="id"
            loading={listLoading || bulkAssigning}
            columns={columns}
            dataSource={visibleLeads}
            tableLayout="auto"
            scroll={{ x: true }}
            rowSelection={
              canBulkAssign
                ? {
                    selectedRowKeys: selectedLeadKeys,
                    onChange: (keys) => setSelectedLeadKeys(keys),
                    preserveSelectedRowKeys: true,
                    getCheckboxProps: () => ({
                      disabled: bulkAssigning,
                    }),
                  }
                : undefined
            }
            locale={{
              emptyText: listError
                ? " "
                : isDocketsVariant
                  ? "עדיין אין דוקטים מהזמנות ששולמו או ממתינות להעברה בנקאית"
                  : "אין לידים להצגה",
            }}
            pagination={{
              pageSize: 20,
              showSizeChanger: false,
              showTotal: (total) =>
                isDocketsVariant ? `${total} דוקטים` : `${total} לידים`,
            }}
            onRow={(record) => ({
              onClick: () => openLead(record),
              style: { cursor: "pointer" },
            })}
          />
        )}
      </div>
    </div>
  );
}

export {
  formatPublicLeadCategory,
  formatLeadSourceType,
  isCrmDocketLead,
  isCrmLeadStatusNew,
  countNewCrmProspectLeads,
} from "./crmLeadFormatters";
